import * as THREE from 'three'
import Terrain, { BlockType, isFluidType } from './terrain'
import Block from './terrain/mesh/block'
import BlockDropManager from './drops'
import MobManager from './mobs'
import Audio from './audio'

interface Fuse {
  x: number
  y: number
  z: number
  timer: number
  mesh: THREE.Mesh
  overlay: THREE.Mesh
  overlayMaterial: THREE.MeshBasicMaterial
}

interface Burst {
  mesh: THREE.Mesh
  material: THREE.MeshBasicMaterial
  ttl: number
}

const BLOCK_RADIUS = 3.5
const ENTITY_RADIUS = 6.5
const MAX_ENTITY_DAMAGE = 20
const FUSE_SECONDS = 2
const BURST_SECONDS = 0.35
const DROP_CHANCE = 0.3

export default class ExplosionManager {
  constructor(
    scene: THREE.Scene,
    terrain: Terrain,
    dropManager: BlockDropManager,
    mobManager: MobManager,
    audio: Audio,
    hooks: {
      getPlayerPosition: () => THREE.Vector3
      damagePlayer: (amount: number, impulse: THREE.Vector3) => void
    }
  ) {
    this.scene = scene
    this.terrain = terrain
    this.dropManager = dropManager
    this.mobManager = mobManager
    this.audio = audio
    this.hooks = hooks
  }

  scene: THREE.Scene
  terrain: Terrain
  dropManager: BlockDropManager
  mobManager: MobManager
  audio: Audio
  hooks: {
    getPlayerPosition: () => THREE.Vector3
    damagePlayer: (amount: number, impulse: THREE.Vector3) => void
  }

  fuses: Fuse[] = []
  bursts: Burst[] = []
  matrixScratch = new THREE.Matrix4()
  zeroMatrix = new THREE.Matrix4().set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0)
  impulseScratch = new THREE.Vector3()
  dropPositionScratch = new THREE.Vector3()
  dirtyMeshes = new Set<THREE.InstancedMesh>()

  // prime a TNT block: tombstone the record and replace the block with a
  // blinking fuse entity (the caller hides the block's own instance)
  ignite = (position: THREE.Vector3, fuseSeconds = FUSE_SECONDS) => {
    const x = Math.round(position.x)
    const y = Math.round(position.y)
    const z = Math.round(position.z)

    const custom = this.terrain.getCustomBlock(x, y, z)
    if (custom) {
      custom.placed = false
    } else {
      this.terrain.recordCustomBlock(new Block(x, y, z, BlockType.tnt, false))
    }

    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      this.terrain.materials.get(this.terrain.materialType[BlockType.tnt])
    )
    mesh.position.set(x, y, z)

    const overlayMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false
    })
    const overlay = new THREE.Mesh(new THREE.BoxGeometry(1.04, 1.04, 1.04), overlayMaterial)
    overlay.position.set(x, y, z)

    this.scene.add(mesh)
    this.scene.add(overlay)
    this.fuses.push({ x, y, z, timer: fuseSeconds, mesh, overlay, overlayMaterial })
    this.audio.playSound(BlockType.tnt)
  }

  update = (delta: number) => {
    for (let i = this.fuses.length - 1; i >= 0; i--) {
      const fuse = this.fuses[i]
      fuse.timer -= delta
      // accelerating white blink
      const blink = Math.sin(fuse.timer * (10 + (FUSE_SECONDS - fuse.timer) * 14))
      fuse.overlayMaterial.opacity = blink > 0 ? 0.65 : 0

      if (fuse.timer <= 0) {
        this.disposeFuse(fuse)
        this.fuses.splice(i, 1)
        this.explode(fuse.x, fuse.y, fuse.z)
      }
    }

    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const burst = this.bursts[i]
      burst.ttl -= delta
      if (burst.ttl <= 0) {
        this.scene.remove(burst.mesh)
        burst.mesh.geometry.dispose()
        burst.material.dispose()
        this.bursts.splice(i, 1)
        continue
      }
      const life = 1 - burst.ttl / BURST_SECONDS
      const scale = 1 + life * (BLOCK_RADIUS * 2.2)
      burst.mesh.scale.set(scale, scale, scale)
      burst.material.opacity = 0.85 * (1 - life)
    }
  }

  disposeFuse = (fuse: Fuse) => {
    this.scene.remove(fuse.mesh)
    this.scene.remove(fuse.overlay)
    fuse.mesh.geometry.dispose()
    fuse.overlay.geometry.dispose()
    fuse.overlayMaterial.dispose()
  }

  explode = (cx: number, cy: number, cz: number) => {
    const radius = BLOCK_RADIUS
    const radiusSq = radius * radius
    const bound = Math.ceil(radius)
    // vanilla: explosions under water destroy no blocks
    const waterlogged = this.terrain.isWaterAt(cx, cy, cz)
    this.dirtyMeshes.clear()

    if (!waterlogged) {
      for (let dx = -bound; dx <= bound; dx++) {
        for (let dy = -bound; dy <= bound; dy++) {
          for (let dz = -bound; dz <= bound; dz++) {
            if (dx * dx + dy * dy + dz * dz > radiusSq) continue
            const x = cx + dx
            const y = cy + dy
            const z = cz + dz
            if (y <= 0) continue

            const custom = this.terrain.getCustomBlock(x, y, z)
            let type: BlockType | null = null
            let isCustom = false
            if (custom) {
              if (!custom.placed) continue
              type = custom.type
              isCustom = true
            } else if (
              this.terrain.isNaturalWaterAt(x, y, z) ||
              this.terrain.isNaturalLavaAt(x, y, z)
            ) {
              continue
            } else if (this.terrain.isNaturalSolidAt(x, y, z)) {
              type = this.terrain.getNaturalBlockType(x, y, z)
            } else {
              continue
            }

            // blast-resistant and fluid cells survive
            if (type === BlockType.bedrock || type === BlockType.obsidian) continue
            if (isFluidType(type)) continue

            // chain reaction: TNT in the blast lights with a short fuse
            if (type === BlockType.tnt) {
              this.clearVisibleInstance(x, y, z, type)
              this.ignite(
                this.dropPositionScratch.set(x, y, z),
                0.4 + Math.random() * 0.6
              )
              continue
            }

            if (isCustom) {
              custom!.placed = false
            } else {
              this.terrain.recordCustomBlock(new Block(x, y, z, type, false))
            }
            this.clearVisibleInstance(x, y, z, type)

            if (this.dropManager && Math.random() < DROP_CHANCE) {
              const dropType =
                type === BlockType.stone ? BlockType.cobblestone : type
              this.dropManager.spawn(
                this.dropPositionScratch.set(x, y, z),
                dropType
              )
            }
          }
        }
      }
    }

    for (const mesh of this.dirtyMeshes) {
      mesh.instanceMatrix.needsUpdate = true
    }

    // entity damage falls off linearly with distance (works underwater too)
    const playerPos = this.hooks.getPlayerPosition()
    const playerDistance = Math.sqrt(
      (playerPos.x - cx) ** 2 + (playerPos.y - cy) ** 2 + (playerPos.z - cz) ** 2
    )
    if (playerDistance < ENTITY_RADIUS) {
      const falloff = 1 - playerDistance / ENTITY_RADIUS
      this.impulseScratch
        .set(playerPos.x - cx, playerPos.y - cy, playerPos.z - cz)
        .normalize()
        .multiplyScalar(14 * falloff)
      this.impulseScratch.y += 5 * falloff
      this.hooks.damagePlayer(Math.ceil(MAX_ENTITY_DAMAGE * falloff), this.impulseScratch)
    }

    for (const mob of this.mobManager.mobs) {
      if (!mob.isAlive || mob.isDying) continue
      const distance = mob.mesh.position.distanceTo(
        this.dropPositionScratch.set(cx, cy, cz)
      )
      if (distance >= ENTITY_RADIUS) continue
      const falloff = 1 - distance / ENTITY_RADIUS
      this.impulseScratch
        .copy(mob.mesh.position)
        .sub(this.dropPositionScratch)
      mob.takeDamage(Math.ceil(MAX_ENTITY_DAMAGE * falloff), this.impulseScratch)
    }

    // fireball flash
    const burstMaterial = new THREE.MeshBasicMaterial({
      color: 0xffa733,
      transparent: true,
      opacity: 0.85,
      depthWrite: false
    })
    const burst = new THREE.Mesh(new THREE.SphereGeometry(1, 12, 12), burstMaterial)
    burst.position.set(cx, cy, cz)
    this.scene.add(burst)
    this.bursts.push({ mesh: burst, material: burstMaterial, ttl: BURST_SECONDS })

    this.audio.playSound(BlockType.stone)

    // water/lava pours into the crater
    this.terrain.fluid.notifyRegion(
      cx - bound, cy - bound, cz - bound,
      cx + bound, cy + bound, cz + bound
    )

    // full regen rebuilds custom-placed instances we couldn't zero directly
    // and exposes the freshly uncovered interior blocks
    this.terrain.generate()
  }

  // worker-generated instances are addressable via idMap; the recorded index
  // does not say which mesh it belongs to, so validate by position before
  // zeroing (surface types can differ between worker and main thread)
  clearVisibleInstance = (x: number, y: number, z: number, hint: BlockType) => {
    const index = this.terrain.idMap.get(this.terrain.getBlockKey(x, y, z))
    if (index === undefined) return
    const candidates = [
      hint,
      BlockType.grass,
      BlockType.sand,
      BlockType.dirt,
      BlockType.stone,
      BlockType.coal,
      BlockType.tree,
      BlockType.leaf,
      BlockType.tnt
    ]
    for (const type of candidates) {
      const mesh = this.terrain.blocks[type]
      if (!mesh || index >= mesh.count) continue
      mesh.getMatrixAt(index, this.matrixScratch)
      const e = this.matrixScratch.elements
      if (
        e[0] !== 0 &&
        Math.abs(e[12] - x) < 0.51 &&
        Math.abs(e[13] - y) < 0.51 &&
        Math.abs(e[14] - z) < 0.51
      ) {
        mesh.setMatrixAt(index, this.zeroMatrix)
        this.dirtyMeshes.add(mesh)
        return
      }
    }
  }

  clear = () => {
    for (const fuse of this.fuses) this.disposeFuse(fuse)
    this.fuses.length = 0
    for (const burst of this.bursts) {
      this.scene.remove(burst.mesh)
      burst.mesh.geometry.dispose()
      burst.material.dispose()
    }
    this.bursts.length = 0
  }
}
